import React, { useMemo, ReactElement, useEffect } from 'react';

import { useSubmitClaimWorkflow } from '../../claims-center/submit-claims/useSubmitClaimWorkflow';
import { useSelectedShopId } from '../../../hooks/useSelectedShop';
import { useClaimsShopCarriers } from './common/useClaimsShopCarriers';
import { useIsOrderItemsRequired } from './common/useIsOrderItemsRequired';
import useInvoiceExtraction from './common/useInvoiceExtraction';
import { useImportDocuments } from './common/useImportDocuments';
import { useDocumentsViewer } from './documents-viewer/useDocumentsViewer';

import { ClaimType, ClaimContext, OnChangeClaimContext, ClaimShopCarrier } from './types';
import { ClaimStepContainerProps } from './common/ClaimStepContainer';

import InvestigationStepContainer from './investigation/InvestigationStepContainer';
import PartialDamageStepContainer from './partial-damage/PartialDamageStepContainer';
import MissingItemStepContainer from './missing-item/MissingItemStepContainer';
import WholeDamageStepContainer from './whole-damage/WholeDamageStepContainer';
import CashOnDeliveryStepContainer from './cash-on-delivery/CashOnDeliveryStepContainer';

import Switch from '../../Wizard/Switch';
import ChooseClaimTypeStep from './ChooseClaimTypeStep';
import ChooseDamageTypeStep from './ChooseDamageTypeStep';
import InvestigationDeliveryInfoStep from './investigation/InvestigationDeliveryInfoStep';
import PartialDamageInfoStep from './partial-damage/PartialDamageInfoStep';
import WholeDamageInfoStep from './whole-damage/WholeDamageInfoStep';
import MissingItemInfoStep from './missing-item/MissingItemInfoStep';
import OutsideOfDeadlineStep from './common/OutsideOfDeadlineStep';
import DeadlineShippingInfoStep from './common/DeadlineShippingInfoStep';
import WarehouseDeliveryConfirmationStep from './common/WarehouseDeliveryConfirmationStep';
import ManualDeadlineCheckStep from './common/ManualDeadlineCheckStep';
import InvestigationDeliveredToMailboxStep from './investigation/InvestigationDeliveredToMailboxStep';
import ShippingInfoStep from './common/ShippingInfoStep';
import EasyShippingInfoStep from './common/EasyShippingInfoStep';
import InvoiceInfoStep from './common/InvoiceInfoStep';
import SearchClaimInfoStep from './common/SearchClaimInfoStep';
import CashOnDeliveryPaymentInfoStep from './cash-on-delivery/CashOnDeliveryPaymentInfoStep';
import CashOnDeliveryProofOfPaymentStep from './cash-on-delivery/CashOnDeliveryProofOfPaymentStep';
import DocumentsViewer from './documents-viewer/DocumentsViewer';
import InvoiceStep from './common/InvoiceStep';
import AdditionalInfoStep from './common/AdditionalInfoStep';
import { StateValue } from 'xstate';
import DraftLoadingStep from './common/DraftLoadingStep';
import DORSigningLinkStep from './investigation/DORSigningLinkStep';

interface StepContainerMap {
  [key: string]: React.ComponentType<ClaimStepContainerProps>;
}

const stepContainersByClaimType: StepContainerMap = {
  [ClaimType.INVESTIGATION]: InvestigationStepContainer,
  [ClaimType.PARTIAL_DAMAGE]: PartialDamageStepContainer,
  [ClaimType.WHOLE_DAMAGE]: WholeDamageStepContainer,
  [ClaimType.MISSING_ITEM]: MissingItemStepContainer,
  [ClaimType.CASH_ON_DELIVERY]: CashOnDeliveryStepContainer,
};

interface CreateClaimProps {
  claim: ClaimContext;
  nonClaimableMessage?: string;
  machine?: any;
  onChange?: OnChangeClaimContext;
  onSubmit?: () => void;
  onSaveDraft?: () => void;
  resetClaim?: () => void;
  onLoadShipmentByTrackingId?: (trackingId: string) => void;
  onResetShipmentSearch?: () => void;
  onChangeTrackingCode?: (code: string) => void;
  onClose?: (step: StateValue) => void;
  isSubmitting?: boolean; // indicates 2 things - search is in progress or claim is being submitted
  isClaimDraftSaving?: boolean;
  isDisabled?: boolean;
  isClaimsAutomated?: boolean;
}

export interface StepProps extends ClaimStepContainerProps {
  shopId: number;
  claim: ClaimContext;
  nonClaimableMessage?: string;
  as: React.ComponentType<any> | typeof React.Fragment;
  onChange: OnChangeClaimContext;
  onResetClaim: () => void;
  onResetShipmentSearch?: () => void;
  onLoadShipmentByTrackingId?: (trackingId: string) => void;
  onChangeTrackingCode?: (code: string) => void;
  isClaimsAutomated?: boolean;
  isManual: boolean;
  carriers: ClaimShopCarrier[];
}

// used by parcel finder and claims center
function CreateClaim(props: CreateClaimProps): ReactElement {
  const {
    claim,
    nonClaimableMessage,
    onChange = () => {},
    onSubmit,
    onSaveDraft,
    resetClaim = () => {},
    onLoadShipmentByTrackingId,
    onResetShipmentSearch,
    onChangeTrackingCode,
    isSubmitting, // indicates 2 things - search is in progress or claim is being submitted
    isClaimDraftSaving,
    isDisabled,
    isClaimsAutomated,
  } = props;

  const shopId = useSelectedShopId();

  const { workflowStep, transition, next, back, canonicalWorkflowStep } = useSubmitClaimWorkflow(claim);

  const onResetClaim = (): void => {
    resetClaim();
  };

  const onSelectDamage = (): void => transition('SELECT_DAMAGE');

  const createOnSelectClaimType = (event: string, type: ClaimType) => (): void => {
    transition(event, { context: { type } });
    onChange({ type });
  };

  const onSelectInvestigation = createOnSelectClaimType('SELECT_INVESTIGATION', ClaimType.INVESTIGATION);
  const onSelectPartialDamage = createOnSelectClaimType('SELECT_PARTIAL_DAMAGE', ClaimType.PARTIAL_DAMAGE);
  const onSelectWholeDamage = createOnSelectClaimType('SELECT_WHOLE_DAMAGE', ClaimType.WHOLE_DAMAGE);
  const onSelectMissingItem = createOnSelectClaimType('SELECT_MISSING_ITEM', ClaimType.MISSING_ITEM);
  const onSelectCashOnDelivery = createOnSelectClaimType('SELECT_CASH_ON_DELIVERY', ClaimType.CASH_ON_DELIVERY);
  const onClose = (): void => props.onClose?.(canonicalWorkflowStep);

  const { data: carriers = [] } = useClaimsShopCarriers();

  // finds selected carrier and adds it to the claim context
  useEffect(() => {
    const selectedCarrier = carriers.find(
      ({ code, countryCode }) => code === claim.carrierCode && countryCode === claim.carrierCountryCode
    );
    onChange({
      carrier: selectedCarrier,
    });
  }, [carriers, claim.carrierCode, claim.carrierCountryCode, onChange]);

  const { isLoading: isLoadingIsOrderItemsRequired } = useIsOrderItemsRequired(
    claim.type,
    onChange,
    claim.isReturnShipment,
    claim.orderId,
    claim.isMultiShipmentOrder
  );
  const { isExtractingInvoice, isFailedToExtractInvoice, extractInvoiceData } = useInvoiceExtraction(onChange);

  const isDORRequired = useMemo(() => claim.type === 'investigation' && !!claim.isDelivered, [
    claim.type,
    claim.isDelivered,
  ]);

  const { onSearchClaimNext, importDORState, importInvoiceState } = useImportDocuments({
    shopId,
    claim,
    isDORRequired,
    next,
    onChange,
    extractInvoiceData,
  });

  const {
    documentsViewerButtonElement,
    documentViewerFiles,
    isDocumentsViewerEnabled,
    isDocumentsViewerOpen,
  } = useDocumentsViewer({ claim, workflowStep });

  const stepProps: StepProps = {
    shopId,
    claim,
    carriers,
    nonClaimableMessage,
    as: (claim.type && stepContainersByClaimType[claim.type]) || React.Fragment,
    onChange,
    onNext: next,
    onBack: back,
    onClose,
    onSaveDraft,
    headerLeftElement: documentsViewerButtonElement,
    onResetClaim,
    onResetShipmentSearch,
    onLoadShipmentByTrackingId,
    onChangeTrackingCode,
    isClaimDraftSaving,
    isSubmitting,
    isDisabled,
    isClaimsAutomated,
    isManual: !claim.shipmentId,
    isLoadingIsOrderItemsRequired,
    isExtractingInvoice,
    isFailedToExtractInvoice,
    extractInvoiceData,
    isDORRequired,
    isNewSubmissionFlow: claim.isNewSubmissionFlow,
  };

  return (
    <React.Fragment>
      {isDocumentsViewerEnabled && isDocumentsViewerOpen && <DocumentsViewer files={documentViewerFiles} />}
      <Switch selected={workflowStep}>
        <DraftLoadingStep key="draft-loading" name="draft-loading" {...stepProps} />
        <ChooseClaimTypeStep
          key="choose-claim-type"
          name="choose-claim-type"
          onClickDamage={onSelectDamage}
          onClickMissingItem={onSelectMissingItem}
          onClickInvestigation={onSelectInvestigation}
          onClickCashOnDelivery={onSelectCashOnDelivery}
          claim={claim}
          onClose={onClose}
        />
        <ChooseDamageTypeStep
          key="choose-damage-type"
          name="choose-damage-type"
          onClickPartialDamage={onSelectPartialDamage}
          onClickWholeDamage={onSelectWholeDamage}
          onBack={back}
          onClose={onClose}
        />
        <SearchClaimInfoStep
          key="search-claim-info"
          name="search-claim-info"
          {...stepProps}
          onNext={onSearchClaimNext}
          onSaveDraft={null}
        />
        <ManualDeadlineCheckStep key="manual-deadline-check" name="manual-deadline-check" {...stepProps} />
        <ShippingInfoStep key="shipping-info" name="shipping-info" {...stepProps} />
        <EasyShippingInfoStep key="easy-shipping-info" name="easy-shipping-info" {...stepProps} />
        <OutsideOfDeadlineStep key="outside-of-deadline" name="outside-of-deadline" {...stepProps} />
        <DeadlineShippingInfoStep key="deadline-shipping-info" name="deadline-shipping-info" {...stepProps} />
        <InvestigationDeliveredToMailboxStep
          key="investigation-delivered-to-mailbox"
          name="investigation-delivered-to-mailbox"
          {...stepProps}
        />
        <InvestigationDeliveryInfoStep
          key="investigation-delivery-info"
          name="investigation-delivery-info"
          importDORState={importDORState}
          {...stepProps}
        />
        <CashOnDeliveryProofOfPaymentStep key="proof-of-payment" name="proof-of-payment" {...stepProps} />
        <InvoiceInfoStep
          key="invoice-info"
          name="invoice-info"
          {...stepProps}
          onNext={onSubmit}
          isSubmitButton={true}
          importInvoiceState={importInvoiceState}
        />
        <CashOnDeliveryPaymentInfoStep
          key="cash-on-delivery-payment-info"
          name="cash-on-delivery-payment-info"
          {...stepProps}
          onNext={onSubmit}
          isSubmitButton={true}
        />
        <WarehouseDeliveryConfirmationStep
          key="warehouse-delivery-confirmation-info"
          name="warehouse-delivery-confirmation-info"
          {...stepProps}
        />
        <PartialDamageInfoStep key="partial-damage-info" name="partial-damage-info" {...stepProps} />
        <WholeDamageInfoStep key="whole-damage-info" name="whole-damage-info" {...stepProps} />
        <MissingItemInfoStep key="missing-item-info" name="missing-item-info" {...stepProps} />
        <InvoiceStep key="invoice-step" name="invoice-step" importInvoiceState={importInvoiceState} {...stepProps} />
        <InvoiceStep
          key="invoice-step-final"
          name="invoice-step"
          importInvoiceState={importInvoiceState}
          {...stepProps}
          onNext={onSubmit}
          isSubmitButton={true}
        />
        <AdditionalInfoStep
          key="additional-info-step"
          name="additional-info-step"
          importDORState={importDORState}
          {...stepProps}
          onNext={onSubmit}
          isSubmitButton={true}
        />
        <DORSigningLinkStep key="create-dor-step" name="create-dor-step" {...stepProps} />
      </Switch>
    </React.Fragment>
  );
}

export default React.memo(CreateClaim);
